import {
  DeepReadonlyObject,
  KeyFunctionMap,
  RxCollection,
  RxCollectionCreator,
  RxDocument,
  RxJsonSchema,
} from "rxdb"
import { IssueConclusion } from "src/util/types/dataModels/genericObject"
import { IssuesOnItem } from "src/util/types/dataModels/issue"

//region Types

export interface IssuesOnItemCollectionMethods extends KeyFunctionMap {
  getIssuesTotal: () => Promise<number>
  getLatestIssue: () => Promise<DeepReadonlyObject<IssuesOnItem> | undefined>
}

export type IssueOnItemDocument = RxDocument<IssuesOnItem, KeyFunctionMap>

export type IssuesOnItemCollection = RxCollection<
  IssuesOnItem,
  KeyFunctionMap,
  IssuesOnItemCollectionMethods
>

//endregion

/**
 * Schema for issues on item collections
 */
export const issuesOnItemSchema: RxJsonSchema<IssuesOnItem> = {
  title: "Issues On Item schema",
  description: "Collection of all issues on item",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 }, // Date
    updated_dt: { type: "string", maxLength: 64 }, // Date
    declared_dt: { type: "string", maxLength: 64 }, // Date
    title: { type: "string", maxLength: 100 },
    reference: { type: "string", maxLength: 80 },
    description: { type: "string", maxLength: 2048 },
    concluded_dt: { type: "string", maxLength: 64 }, // Date
    conclusion: {
      type: "string",
      enum: ["closed", "closed_resolved", "rejected"] as IssueConclusion[],
    },
    main_image: { ref: "attachment", type: "string", maxLength: 36 }, // UUID
    superseded_by: { type: "string", maxLength: 36 }, // UUID
    raised_by_user: { type: "string", maxLength: 36 }, // UUID
    concluded_by_user: { type: "string", maxLength: 36 }, // UUID
    work_domain: { ref: "work_domain_workspace", type: "string", maxLength: 36 }, // UUID
    priority: { ref: "issue_priority", type: "string", maxLength: 36 }, // UUID
    actionable_item: { ref: "item", type: "string", maxLength: 36 }, // UUID
    corrective_work_order: { type: "string", maxLength: 36 }, // UUID
    raised_from: { type: "string", maxLength: 36 }, // UUID
    related_procedure_steps_bool: { type: "array", items: { type: "string" } },
    related_procedure_steps_numeric: { type: "array", items: { type: "string" } },
    related_procedure_steps_select: { type: "array", items: { type: "string" } },
    related_procedure_steps_photo: { type: "array", items: { type: "string" } },
    related_procedure_steps_verify: { type: "array", items: { type: "string" } },
    attachments: {
      type: "array",
      items: {
        type: "string",
      },
    },
  },
  required: ["id", "title", "raised_by_user", "work_domain", "priority", "actionable_item"],
  indexes: ["title", "priority", "actionable_item", "updated_dt"],
}

export const issuesOnItemCollectionMethods: IssuesOnItemCollectionMethods = {
  async getIssuesTotal(this: IssuesOnItemCollection): Promise<number> {
    return await this.count().exec()
  },

  async getLatestIssue(
    this: IssuesOnItemCollection
  ): Promise<DeepReadonlyObject<IssuesOnItem> | undefined> {
    const resultsDB: IssueOnItemDocument[] = await this.find().exec()

    // No issues found
    if (resultsDB.length === 0) return

    const latestIssue = resultsDB.reduce((a, b) => {
      return new Date(a.declared_dt || a.created_dt).getTime() >
        new Date(b.declared_dt || b.created_dt).getTime()
        ? a
        : b
    })
    return latestIssue.toJSON()
  },
}

/**
 * A collection creator that can be passed to the database when setting up collections
 */
export const issuesOnItemCollectionCreator: RxCollectionCreator = {
  schema: issuesOnItemSchema,
  statics: issuesOnItemCollectionMethods,
}
